import { Cancel, Check, Close, Edit } from "@mui/icons-material";
import { Button, Chip, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";

import useService from "../../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../../global/ApiRequestErrorHandler";
import { currencyFormatter, truncate } from "../../../../../../global/helpers";

import useRevalidateParent from "./../../../../../../../hooks/useRevalidateParent";
import DynamicInput from "./inputs/DynamicInput";

export default function InlineEditRow({
  apiUrl,
  customDisplayUrl,
  customLimit,
  disabled,
  displayDetail,
  displayNameKey,
  keyVal,
  nullable,
  optionsUrl,
  staticVal,
  title,
  type,
  valueLabels,
}) {
  const enumRepository = useService("repositories/enum-repository");
  const revalidateParent = useRevalidateParent("rvClientDetail");
  const [val, setVal] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [hoverActive, setHoverActive] = useState(false);
  const [search, setSearch] = useState("");
  const [activeEditObj, setActiveEditObj] = useState(null);
  const [responseSuccess, setResponseSuccess] = useState(null);
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!displayDetail || !keyVal) {
      setVal("");
      setActiveEditObj(null);
      return;
    }

    const value = staticVal ?? displayDetail[keyVal] ?? null;

    // helper to normalize id/key comparison
    const matchesId = (item, v) => {
      if (v === null || v === undefined) {
        return false;
      }
      const vid = String(v);
      const candidates = [item?.pk, item?.id, item?.key, item?.value].filter(Boolean);
      return candidates.map(String).includes(vid);
    };

    // 1) multi-select search (array of ids stored in `value`)
    if (type === "multi-search-select") {
      setVal(value ?? []);
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        Array.isArray(valueLabels) &&
        valueLabels.length > 0
      ) {
        const matched = valueLabels.filter((item) => value.some((v) => matchesId(item, v)));
        setActiveEditObj(matched);
      } else if (
        Array.isArray(value) &&
        value.length > 0 &&
        valueLabels &&
        typeof valueLabels === "object" &&
        !Array.isArray(valueLabels)
      ) {
        // valueLabels is a map/object: turn ids into objects with displayNameKey when possible
        const mapped = value
          .map((v) =>
            valueLabels[v] ? { [displayNameKey || "label"]: valueLabels[v], id: v } : null
          )
          .filter(Boolean);
        setActiveEditObj(mapped.length ? mapped : value);
      } else {
        // fallback: keep the raw ids (parent component may handle)
        setActiveEditObj(Array.isArray(value) ? value : []);
      }

      return;
    }

    // 2) customDisplayUrl: options come from remote and valueLabels is an array of objects
    if (customDisplayUrl && Array.isArray(valueLabels) && value) {
      const found = valueLabels.find((item) => matchesId(item, value)) || null;
      setVal(found ? (found?.pk ?? found?.id ?? value) : value);
      setActiveEditObj(found);
      return;
    }

    // 3) valueLabels provided (could be map or array)
    if (valueLabels && value) {
      // valueLabels as map/object: valueLabels[keyVal][value] or valueLabels[value] patterns
      if (typeof valueLabels === "object" && !Array.isArray(valueLabels)) {
        // Try valueLabels[keyVal][value] if nested, else valueLabels[value]
        const nested = valueLabels?.[keyVal];
        if (nested && nested[value] !== undefined) {
          setVal(value);
          setActiveEditObj({ [displayNameKey || "label"]: nested[value] });
          return;
        }
        if (valueLabels[value] !== undefined) {
          setVal(value);
          setActiveEditObj({ [displayNameKey || "label"]: valueLabels[value] });
          return;
        }
      }

      // valueLabels is array: try to find item by id/pk/key
      if (Array.isArray(valueLabels)) {
        const found = valueLabels.find((item) => matchesId(item, value)) || null;
        if (found) {
          setVal(found?.pk ?? found?.id ?? value);
          setActiveEditObj(found);
          return;
        }
      }

      // fallback: set raw value and a minimal display object if displayNameKey available
      setVal(value);
      if (displayNameKey && valueLabels?.[keyVal] && valueLabels[keyVal][value]) {
        setActiveEditObj({ [displayNameKey]: valueLabels[keyVal][value] });
      } else {
        setActiveEditObj(value);
      }

      return;
    }

    // Default: no special labels/options, just set raw value / null
    setVal(value ?? "");
    setActiveEditObj(value ?? null);
  }, [displayDetail, valueLabels, keyVal, staticVal, type, customDisplayUrl, displayNameKey]);

  const handleApiError = (error) => {
    setLoading(false);
    const res = !error.response ? error.response : error.response.data;
    const errArr = ApiRequestErrorHandler(res);
    setResponseBreakdown(errArr[0]);
    setResponseSuccess(false);
  };

  const handleApiSuccess = (response, valueName) => {
    setLoading(false);
    const res = response;
    setResponseBreakdown(`${valueName} was successfully updated`);
    setResponseSuccess(true);
    setIsEditing(false);
    setTimeout(() => {
      setResponseSuccess(null);
      setResponseBreakdown("");
    }, 5000);
    return res;
  };

  async function submitData(title) {
    const postVal = val === "No Selection" || val === "" || !val ? null : val;

    const body = {
      [keyVal]: postVal,
    };
    try {
      const resp = await enumRepository.patchWithUrl({
        urlPath: apiUrl,
        body,
      });
      revalidateParent();
      handleApiSuccess(resp, title);
    } catch (error) {
      handleApiError(error);
    }
  }

  const cancelEdit = () => {
    const initialValue = displayDetail[keyVal];
    setVal(initialValue);
    setIsEditing(false);
    setResponseBreakdown("");
    setResponseSuccess(null);
  };

  const typeDisplayFormat = (value) => {
    // use passed value if provided, otherwise fall back to state `val`
    const v = value === undefined ? val : value;
    const chipColors = ["sky-600", "green-600", "orange-600", "purple-600"];

    const getLabelForItem = (item) => {
      if (item === null || item === undefined) {
        return "";
      }
      // object option
      if (typeof item === "object") {
        if (displayNameKey && item[displayNameKey]) {
          return item[displayNameKey];
        }
        if (item.name) {
          return item.name;
        }
        if (item.label) {
          return item.label;
        }
        if (item.title) {
          return item.title;
        }
        if (item.id !== undefined) {
          return String(item.id);
        }
        if (item.pk !== undefined) {
          return String(item.pk);
        }
        return truncate(JSON.stringify(item), 30);
      }

      // primitive id/string: try to resolve via valueLabels (array or map)
      if (Array.isArray(valueLabels) && valueLabels.length > 0) {
        const found = valueLabels.find(
          (it) => String(it?.pk ?? it?.id ?? it?.key ?? it?.value) === String(item)
        );
        if (found) {
          return getLabelForItem(found);
        }
      } else if (valueLabels && typeof valueLabels === "object") {
        // nested by keyVal or direct map
        if (valueLabels[keyVal] && valueLabels[keyVal][item] !== undefined) {
          return valueLabels[keyVal][item];
        }
        if (valueLabels[item] !== undefined) {
          return valueLabels[item];
        }
      }

      return String(item);
    };

    // empty / null case
    if (v === "" || v === null || (Array.isArray(v) && v.length === 0) || v === undefined) {
      return "Add Value";
    }

    // multi-select
    if (type === "multi-search-select") {
      const arr = Array.isArray(v) ? v : [];
      return arr.length > 0
        ? arr.map((item, idx) => (
            <Chip
              key={idx}
              className={`m-1 w-fit text-sm uppercase bg-${
                chipColors[idx % chipColors.length]
              } text-white`}
              label={getLabelForItem(item)}
            />
          ))
        : "Add Value";
    }

    // dates
    if (type === "date") {
      try {
        const d = new Date(v);
        if (isNaN(d)) {
          return truncate(String(v), 30);
        }
        return d.toLocaleDateString("en-US", { timeZone: "UTC" });
      } catch {
        return truncate(String(v), 30);
      }
    }

    // address object
    if (type === "address" && v) {
      if (typeof v === "object" && v.raw) {
        return v.raw;
      }
      return truncate(String(v), 30);
    }

    // numbers
    if (type === "number") {
      const n = Number(v);
      if (!isNaN(n)) {
        return currencyFormatter.format(n);
      }
      return truncate(String(v), 30);
    }

    // if valueLabels is a map keyed by keyVal or id
    if (valueLabels) {
      // map/object case
      if (typeof valueLabels === "object" && !Array.isArray(valueLabels)) {
        if (valueLabels[keyVal] && valueLabels[keyVal][v] !== undefined) {
          return valueLabels[keyVal][v];
        }
        if (valueLabels[v] !== undefined) {
          return valueLabels[v];
        }
      }

      // array case: try find object and return displayNameKey
      if (Array.isArray(valueLabels) && valueLabels.length > 0) {
        const found = valueLabels.find((it) =>
          [it?.pk, it?.id, it?.key, it?.value].map(String).includes(String(v))
        );
        if (found) {
          return getLabelForItem(found);
        }
      }
    }

    // object value fallback
    if (typeof v === "object") {
      if (displayNameKey && v[displayNameKey]) {
        return v[displayNameKey];
      }
      return truncate(JSON.stringify(v), 30);
    }

    // default
    return truncate(String(v), 30);
  };

  return isEditing ? (
    <div className="mb-2 w-full">
      <div className="mt-2 flex w-full items-center justify-between">
        <div className="w-[80%] self-center">
          <DynamicInput
            setVal={setVal}
            val={val}
            title={title}
            type={type}
            keyVal={keyVal}
            apiUrl={optionsUrl}
            activeEditObj={activeEditObj}
            setSearch={setSearch}
            search={search}
            responseSuccess={responseSuccess}
            responseBreakdown={responseBreakdown}
            displayNameKey={displayNameKey}
            customLimit={customLimit}
            nullable={nullable}
          />
        </div>
        {loading ? (
          <CircularProgress color="secondary" />
        ) : responseSuccess === false ? (
          <Cancel className="text-red-600" />
        ) : (
          ""
        )}
        {responseSuccess ? (
          ""
        ) : (
          <div className="flex w-[20%] flex-wrap justify-end">
            <Tooltip title="Submit">
              <IconButton className="text-green-600" onClick={() => submitData(title)} size="small">
                <Check className="text-[20px]" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton className="text-red-600" onClick={() => cancelEdit()} size="small">
                <Close className="text-[20px]" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
      {responseSuccess === false && responseBreakdown ? (
        <p className="text-[10px] text-red-600">{responseBreakdown}</p>
      ) : (
        ""
      )}
    </div>
  ) : (
    <>
      <div>
        <h2 className="text-gray-400">{title}: </h2>
        <Tooltip title={disabled ? "" : "Click to edit"}>
          <span>
            <Button
              onClick={() => {
                setIsEditing(true);
                setHoverActive(false);
              }}
              disabled={disabled}
              className="break-all text-left normal-case text-black hover:bg-gray-200"
              onMouseEnter={() => setHoverActive(true)}
              onMouseLeave={() => setHoverActive(false)}
            >
              {typeDisplayFormat(val)}{" "}
              <span className="h-[20px] w-[20px]">
                {hoverActive ? <Edit className="text-gray-400" /> : ""}
              </span>
            </Button>
          </span>
        </Tooltip>
      </div>
      <p className="text-[12px]">
        {responseBreakdown} {responseSuccess ? <Check className="text-green-600" /> : ""}
      </p>
    </>
  );
}
